/**
 * @file     LockedLessonCTA.tsx
 * @readme   ../README.md
 * @purpose  Inline "Keep reading" gate shown under a locked lesson's preview. Sign-up and sign-in both return the reader to the same lesson.
 */
'use client';

import '@/core/i18n/deferred/academy';
import Box from '@mui/material/Box';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { HubActionButton } from '@/components/hub/HubActionButton';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { focusRingSx, hairline, headingFont, hubColors } from '@/components/hub/hubKitStyles';

interface LockedLessonCTAProps {
  /** Path of the locked lesson; auth returns here. */
  lessonPath: string;
  audienceSlug?: string;
}

/** Sign-up URL that brings the reader back to the lesson they were reading. */
export function lockedLessonSignUpHref(lessonPath: string, audienceSlug?: string): string {
  const qs = new URLSearchParams({ redirect: lessonPath });
  if (audienceSlug) qs.set('audience', audienceSlug);
  return `/sign-up?${qs.toString()}`;
}

export function LockedLessonCTA({ lessonPath, audienceSlug }: LockedLessonCTAProps) {
  const { t } = useTranslation();
  const signInHref = `/sign-in?${new URLSearchParams({ redirect: lessonPath }).toString()}`;

  return (
    <Box
      component="section"
      aria-labelledby="locked-lesson-gate"
      sx={{
        border: hairline,
        borderRadius: `${layoutTokens.radius.lg}px`,
        backgroundColor: hubColors.surfaceCard,
        p: { xs: 2.5, sm: 3 },
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box
        component="h2"
        id="locked-lesson-gate"
        sx={{ m: 0, fontFamily: headingFont, fontSize: 20, fontWeight: fontWeightTokens.semibold, lineHeight: 1.3, color: hubColors.textPrimary }}
      >
        {t('academy.locked.title', { defaultValue: 'Keep reading' })}
      </Box>
      <Box component="p" sx={{ m: 0, fontSize: 15, lineHeight: 1.55, color: hubColors.textSecondary }}>
        {t('academy.locked.body', {
          defaultValue: 'Create a free account to open the rest of this lesson and save your place.',
        })}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 2, rowGap: 1, pt: 2 }}>
        <HubActionButton
          action={{
            label: t('academy.locked.cta', { defaultValue: 'Create account' }),
            href: lockedLessonSignUpHref(lessonPath, audienceSlug),
          }}
        />
        <Box component="p" sx={{ m: 0, fontSize: 14, color: hubColors.textSecondary }}>
          {t('academy.locked.haveAccount', { defaultValue: 'Already have an account?' })}{' '}
          <Box
            component={Link}
            href={signInHref}
            sx={{
              display: 'inline-block',
              py: 1.5,
              color: hubColors.link,
              fontWeight: fontWeightTokens.medium,
              textDecoration: 'none',
              '@media (hover: hover)': { '&:hover': { color: hubColors.linkHover, textDecoration: 'underline' } },
              ...focusRingSx,
            }}
          >
            {t('academy.locked.signInLink', { defaultValue: 'Sign in' })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
